import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { Button, ButtonFill, ButtonSize, ButtonType } from 'views/mutual/button';
import { CheckBoxGroup, CheckBoxItem } from 'views/mutual/checkBoxGroup';
import { CardLabel, QuestionCard } from 'views/mutual/questionCard';
import { SelectAndTextAnswer, ISelectAndTextQuestion } from 'views/pages/compatibilityCheck/hooks';

type SelectAndTextQuestionPageProps = {
  question: ISelectAndTextQuestion;
  initialAnswer: SelectAndTextAnswer | null;
  onSubmit: (answerValue: SelectAndTextAnswer) => void;
  backQuestion: () => void;
};

const createCheckBoxGroupItems = (
  selections: Array<{
    value: number;
    label: string;
  }>,
  answers: Array<{
    value: number;
    label: string;
  }> | null,
): Array<CheckBoxItem> =>
  selections.map((x) => ({
    label: x.label,
    value: x.value,
    checked: answers?.find((answer) => answer.value === x.value) ? true : false,
  }));

type SelectAndTextQuestionPageInput = {
  answers: Array<CheckBoxItem>;
  otherText: string;
};


const hasOther = (array: Array<CheckBoxItem>,) =>{
  return  array.find(item => item.label === 'その他');
}
const isCheckedOther = (array: Array<CheckBoxItem>, question?: ISelectAndTextQuestion) => {
  return (!question || question.kind === 'selectAndText') && hasOther(array)?.checked;
};

const validate = (input: SelectAndTextQuestionPageInput) => {
  const errors = {} as { [P in keyof SelectAndTextQuestionPageInput]: string | undefined };
  if (input.answers.every((x) => !x.checked)) {
    errors.answers = '選択してください';
  }

  if (isCheckedOther(input.answers) && !input.otherText) {
    errors.otherText = 'その他を選択した場合は記入ください';
  }

  return errors;
};

export const SelectAndTextQuestionPage = (props: SelectAndTextQuestionPageProps) => {
  const initialValues = useMemo<SelectAndTextQuestionPageInput>(() => {
    return {
      answers: createCheckBoxGroupItems(props.question.selections, props.initialAnswer?.values || null),
      otherText: props.initialAnswer?.text || '',
    };
  }, [props.question, props.initialAnswer]);

  // enableReinitialize: trueにすることで同じSelectAndTextの質問が続いても前の値が残らないようにしている
  const formik = useFormik<SelectAndTextQuestionPageInput>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (input: SelectAndTextQuestionPageInput) =>
      props.onSubmit({
        values: input.answers.filter((x) => x.checked),
        text: input.otherText,
      }),
    validate,
    validateOnBlur: false,
    validateOnChange: true, //trueにしてerrorでformik.submitCount > 0を見ることで変更時にエラーを解消する
  });

  useEffect(() => {
    if (!isCheckedOther(formik.values.answers)) {
      formik.setFieldValue('otherText', '');
    }
  }, [formik.values.answers]);

  return (
    <div className="compatibilityCheck">
      <QuestionCard
        topChildren={<CardLabel letter={props.question.questionText} />}
        bottomChildren={
          <>
            <CheckBoxGroup
              items={formik.values.answers}
              setItems={(items) => {
                formik.setFieldValue('answers', items);
              }}
              error={(formik.submitCount > 0 && (formik.errors.answers as string)) || ''}
            />
            {hasOther(formik.values.answers) &&(
              <div className="compatibilityCheck_textWrapper">
                <input
                  className="compatibilityCheck_textWrapper_input"
                  type="text"
                  placeholder="その他の内容を具体的にご記入ください"
                  value={formik.values.otherText}
                  maxLength={25}
                  onChange={(e) => formik.setFieldValue('otherText', e.target.value)}
                  disabled={!isCheckedOther(formik.values.answers)}
                />
                {formik.submitCount > 0 && formik.errors.otherText && (
                  <div className="compatibilityCheck_textWrapper_error">{formik.errors.otherText}</div>
                )}
              </div>
            )}            
          </>
        }
      />
      <div className="compatibilityCheck_buttons">
        <Button
          type={ButtonType.Submit}
          label="次へ"
          buttonFill={ButtonFill.black}
          buttonSize={ButtonSize.medium}
          onClick={formik.handleSubmit}
        />
        <Button
          label="前の画面に戻る"
          buttonFill={ButtonFill.link}
          buttonSize={ButtonSize.small}
          onClick={props.backQuestion}
        />
      </div>
    </div>
  );
};
